import axios from 'axios';
import { ApiConfig } from './api';
import { AuthResponse } from '../types/user';

const OIDC_STATE_KEY = 'cqi_oidc_state';
const OIDC_CODE_VERIFIER_KEY = 'cqi_oidc_code_verifier';
const OIDC_CALLBACK_PROCESSING_KEY = 'cqi_oidc_callback_processing';

export interface OidcConfig {
  issuerUrl: string;
  clientId: string;
  redirectUri: string;
  scopes: string;
}

export const getOidcConfig = (): OidcConfig | null => {
  const issuerUrl = process.env.REACT_APP_REGISTRY_OIDC_ISSUER;
  const clientId = process.env.REACT_APP_REGISTRY_OIDC_CLIENT_ID;
  const redirectUri = process.env.REACT_APP_REGISTRY_OIDC_REDIRECT_URI;
  const scopes = process.env.REACT_APP_REGISTRY_OIDC_SCOPES || 'openid email';

  console.log(issuerUrl, clientId, redirectUri, scopes);
  if (!issuerUrl || !clientId || !redirectUri) {
    return null;
  }

  return { issuerUrl, clientId, redirectUri, scopes };
};

const base64UrlEncode = (buffer: Uint8Array): string => {
  const bytes = Array.from(buffer)
    .map(byte => String.fromCharCode(byte))
    .join('');
  return btoa(bytes)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

export const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

export const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
};

export const generateState = (): string => {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

/**
 * Redirect the browser to the CHW Registry OIDC authorization endpoint.
 */
export const initiateOidcLogin = async (): Promise<void> => {
  const config = getOidcConfig();
  if (!config) {
    throw new Error('OIDC is not configured');
  }

  const state = generateState();
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Clear any stale callback processing flag before starting a fresh login
  sessionStorage.removeItem(OIDC_CALLBACK_PROCESSING_KEY);
  sessionStorage.setItem(OIDC_STATE_KEY, state);
  sessionStorage.setItem(OIDC_CODE_VERIFIER_KEY, codeVerifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scopes,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256'
  });

  window.location.href = `${config.issuerUrl}/auth?${params.toString()}`;
};

/**
 * Exchange an OIDC authorization code for a local application session.
 */
export const completeOidcLogin = async (code: string, state: string): Promise<void> => {
  const config = getOidcConfig();
  if (!config) {
    throw new Error('OIDC is not configured');
  }

  const storedState = sessionStorage.getItem(OIDC_STATE_KEY);
  const codeVerifier = sessionStorage.getItem(OIDC_CODE_VERIFIER_KEY);

  sessionStorage.removeItem(OIDC_STATE_KEY);
  sessionStorage.removeItem(OIDC_CODE_VERIFIER_KEY);

  if (!storedState || storedState !== state) {
    throw new Error('Invalid OIDC state parameter');
  }

  if (!codeVerifier) {
    throw new Error('Missing OIDC code verifier');
  }

  const response = await axios.post<AuthResponse>(`${ApiConfig.baseUrl}/users/oidc/callback`, {
    code,
    codeVerifier
  });

  if (response.data.token) {
    localStorage.setItem('cqi_token', response.data.token);
    localStorage.setItem('cqi_user', JSON.stringify(response.data.user));
  }
};
